import type { SeoFaqItem } from "./seo-content";

export type EnglishCompareSlug =
  | "hotline-vs-mcp"
  | "hotline-vs-skills"
  | "hotline-vs-api"
  | "hotline-vs-saas";

export type EnglishCompareDimension = {
  label: string;
  hotline: string;
  other: string;
  why: string;
};

export type EnglishComparePage = {
  slug: EnglishCompareSlug;
  path: `/en/compare/${EnglishCompareSlug}/`;
  title: string;
  description: string;
  eyebrow: string;
  otherName: string;
  otherColor: string;
  tldr: string;
  takeaways: string[];
  dimensions: EnglishCompareDimension[];
  whenHotline: string;
  whenOther: string;
  faqs: SeoFaqItem[];
  nextLinks: Array<{ href: string; label: string; tone?: "primary" | "secondary" }>;
};

const NEXT_LINKS_DEFAULT: EnglishComparePage["nextLinks"] = [
  { href: "/en/docs/", label: "Read the docs overview", tone: "primary" },
  { href: "/marketplace", label: "Browse Marketplace", tone: "secondary" },
  { href: "/docs/quick-start-caller", label: "Caller Quick Start (ZH)", tone: "secondary" },
];

export const EN_COMPARE_PAGES: EnglishComparePage[] = [
  {
    slug: "hotline-vs-mcp",
    path: "/en/compare/hotline-vs-mcp/",
    title: "Hotline vs MCP Server | CALL ANYTHING",
    description:
      "MCP is great for agent-runtime tool access. Hotline adds the product layer above it — identity, discovery, versioning, ratings, and settlement. They are complementary.",
    eyebrow: "Compare · Hotline vs MCP",
    otherName: "MCP Server",
    otherColor: "#FACC15",
    tldr:
      "MCP handles tool access — how an agent runtime discovers and invokes tools. Hotline handles capability business shape — how a capability is productized, discovered, and settled. A Hotline can expose an MCP surface, but product routing and settlement live at the Hotline layer. They are complementary, not competing.",
    takeaways: [
      "MCP solves tool access; Hotline solves capability productization — they complement each other.",
      "A Hotline can expose an MCP surface; product routing and settlement happen at the Hotline layer.",
      "Identity, discovery, versioning, ratings, and settlement typically need a product layer above MCP — Hotline provides that layer.",
    ],
    dimensions: [
      {
        label: "Primary job",
        hotline: "Package a capability for discovery, calling, and settlement.",
        other: "Connect tools to an agent runtime.",
        why: "One focuses on product shape, the other on runtime plumbing — different problems.",
      },
      {
        label: "Identity & trust",
        hotline: "Protocol-level responder identity supports ratings and trust evaluation.",
        other: "Great for agent-runtime tool access; identity, discovery, ratings, and settlement are typically handled by a product layer above.",
        why: "Capabilities going to market need seller-side identity; MCP focuses elsewhere.",
      },
      {
        label: "Settlement",
        hotline: "Per-call settlement at the protocol layer.",
        other: "Focused on tool invocation plumbing; billing logic is left to the product layer above.",
        why: "Capabilities that need direct monetization require protocol-level settlement.",
      },
      {
        label: "Discovery",
        hotline: "Designed for public or team-level discovery via Marketplace.",
        other: "Distributed by agent config or documentation; discovery model is up to the consumer.",
        why: "Discovery is what turns a local integration into a capability catalog.",
      },
    ],
    whenHotline:
      "When the capability needs to be productized — identity, per-call settlement, version management, and public ratings; or when agent teams need a discoverable, subscribable capability distribution layer.",
    whenOther:
      "When you only need runtime tool access without product packaging (discovery, settlement, ratings). MCP is clean and efficient at the tool-access layer.",
    faqs: [
      {
        question: "Do I need to rewrite an existing MCP server to use Hotline?",
        answer:
          "No. A wrapper lets the existing implementation keep its runtime surface while gaining Hotline's identity, billing, versioning, and ratings layer.",
      },
      {
        question: "Can a Hotline still be consumed by MCP-friendly agents?",
        answer:
          "Yes. MCP can remain one of the access surfaces while the Hotline stays the canonical product and routing abstraction.",
      },
      {
        question: "When does MCP alone make more sense?",
        answer:
          "When you only need a lightweight runtime tool surface and don't need seller identity, settlement, or marketplace behavior. MCP is purpose-built for that job.",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },
  {
    slug: "hotline-vs-skills",
    path: "/en/compare/hotline-vs-skills/",
    title: "Hotline vs Agent Skills | CALL ANYTHING",
    description:
      "Skills are great for injecting instructions, workflows, and preferences into model context. Hotline keeps execution logic at the Responder side and exposes only the contract. They solve different layers and compose naturally.",
    eyebrow: "Compare · Hotline vs Skills",
    otherName: "Agent Skills",
    otherColor: "#8B5CF6",
    tldr:
      "Skills are great for behavior injection and workflow guidance — putting instructions, processes, and preferences into model context. Hotline is designed for capability productization — private implementation stays outside the context, only the contract is exposed. Skills can call Hotlines as an execution backend. They solve different layers of the problem.",
    takeaways: [
      "Skills guide behavior inside model context; Hotline provides callable capabilities outside context — different layers.",
      "Skills can call Hotlines as an execution backend — they compose naturally.",
      "Commercialization, versioning, ratings, and settlement need to be built outside Skills; Hotline provides that layer.",
    ],
    dimensions: [
      {
        label: "Execution location",
        hotline: "Runs at the Responder side; model sees only input/output schema.",
        other: "Great for injecting instructions, workflows, and preferences into model context for in-context execution.",
        why: "Execution location determines whether implementation logic needs to enter the prompt.",
      },
      {
        label: "Prompt footprint",
        hotline: "Near-zero prompt footprint beyond discovery metadata.",
        other: "Consumes prompt space and token budget; well-suited for lightweight behavioral guidance.",
        why: "Affects how many capabilities an agent can carry in long conversations.",
      },
      {
        label: "Commercial model",
        hotline: "Per-call settlement at the protocol layer.",
        other: "Great for sharing behavioral guidance freely; commercialization, versioning, ratings, and settlement need to be built externally.",
        why: "Capabilities that need monetization require protocol-level settlement.",
      },
      {
        label: "Source visibility",
        hotline: "Implementation remains black-boxed behind a contract.",
        other: "Instruction logic is visible to the model — by design, for behavior steering.",
        why: "Different visibility models serve different purposes.",
      },
    ],
    whenHotline:
      "When the capability's core implementation needs protection, monetization, or has high execution cost that shouldn't be borne by model context.",
    whenOther:
      "When your goal is to steer model behavior — injecting instructions, workflow preferences, and process guidance into context. Skills are highly efficient at behavior injection and workflow guidance.",
    faqs: [
      {
        question: "Can a Skill call a Hotline?",
        answer:
          "Yes, and it's a recommended composition pattern. The Skill teaches the model when and why to call a capability; the Hotline handles the actual execution and settlement.",
      },
      {
        question: "Does Hotline replace every use of Skills?",
        answer:
          "No. Skills are still the right tool for instruction, orchestration hints, and local behavior shaping. Hotline becomes valuable when the capability itself is the product or needs runtime isolation.",
      },
      {
        question: "What's the best way to combine them?",
        answer:
          "Use a Skill to tell the model a capability exists and when to invoke it; use the Hotline for the protected execution and settlement. A Hotline can ship an official Skill template that callers drop into their project.",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },
  {
    slug: "hotline-vs-api",
    path: "/en/compare/hotline-vs-api/",
    title: "Hotline vs Traditional API | CALL ANYTHING",
    description:
      "APIs are a service interface standard, great for companies and platforms providing stable services. Hotline focuses on how individuals or small teams package capabilities into discoverable, callable contracts.",
    eyebrow: "Compare · Hotline vs API",
    otherName: "Traditional API",
    otherColor: "#3B82F6",
    tldr:
      "An API is a service interface standard, well-suited for companies and platforms providing stable services. A Hotline is a capability productization contract for individuals or small teams — adding identity, discovery, versioning, metering, and ratings. A Hotline can be exposed as an OpenAPI endpoint. They serve different supplier populations and solve different layers of the problem.",
    takeaways: [
      "APIs are interface standards; Hotlines are capability productization contracts — different layers.",
      "A Hotline can be exposed as an OpenAPI endpoint — they coexist naturally.",
      "Hotline adds identity, discovery, versioning, metering, and ratings, aimed at individual or small-team capability distribution.",
    ],
    dimensions: [
      {
        label: "Target supplier",
        hotline: "Individuals or small teams; a personal identity is enough to publish.",
        other: "Well-suited for companies and platforms with established business operations and support infrastructure.",
        why: "Hotline lets individuals productize capabilities; APIs are a natural fit for organizational service delivery.",
      },
      {
        label: "Client integration",
        hotline: "Pull catalog, call directly; shared protocol-level client.",
        other: "Read OpenAPI / SDK, write client, handle auth and rate limiting.",
        why: "Unified protocol integration costs less when calling many suppliers.",
      },
      {
        label: "Result shape",
        hotline: "All Hotlines share a result_package; agents learn once.",
        other: "Each API defines its own schema — flexible but requires per-provider adaptation.",
        why: "Affects whether agents can generalize the 'call external capability' action.",
      },
      {
        label: "Discovery path",
        hotline: "Catalog and marketplace oriented.",
        other: "Docs portal and vendor signup oriented.",
        why: "Agents need capability discovery, not only endpoint documentation.",
      },
    ],
    whenHotline:
      "When you are an individual or small team wanting to productize capabilities — with identity, discovery, versioning, and per-call settlement; or when agent teams want one client to call many suppliers uniformly.",
    whenOther:
      "When you are a company or platform with established OpenAPI / SDK / business contract systems serving stable client relationships — APIs are mature and efficient in that scenario.",
    faqs: [
      {
        question: "Does Hotline replace APIs?",
        answer:
          "No. APIs remain a common implementation surface. Hotline sits above them as the capability contract that agents interact with. A Hotline can wrap existing API services.",
      },
      {
        question: "Can a Hotline be exposed as an OpenAPI endpoint?",
        answer:
          "Yes. A Hotline can be exposed via an HTTP adapter as a standard OpenAPI endpoint while keeping its protocol-level identity and settlement.",
      },
      {
        question: "Is Hotline only for public marketplaces?",
        answer:
          "No. The same abstraction is useful inside a team or private deployment whenever many capabilities need a shared external-call contract.",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },
  {
    slug: "hotline-vs-saas",
    path: "/en/compare/hotline-vs-saas/",
    title: "Hotline vs SaaS | CALL ANYTHING",
    description:
      "SaaS is great for companies providing continuously operated services. Hotline is a standardized packaging for individual capabilities. They serve different supplier populations, and SaaS companies can adopt the Hotline protocol too.",
    eyebrow: "Compare · Hotline vs SaaS",
    otherName: "SaaS",
    otherColor: "#F97316",
    tldr:
      "SaaS is how companies deliver continuously operated software services, with full account, subscription, and operations infrastructure. Hotline is a standardized packaging for individual capabilities — protocol-level identity, billing, discovery, and consoles included. They serve different supplier populations. SaaS companies can also adopt the Hotline protocol to expose agent-callable capabilities.",
    takeaways: [
      "SaaS serves company-level continuous operations; Hotline serves individual capability productization — different supplier populations.",
      "SaaS companies can adopt Hotline protocol to expose capabilities as an agent channel.",
      "Marketplace and Consoles provide out-of-the-box distribution and operations infrastructure for individual capability suppliers.",
    ],
    dimensions: [
      {
        label: "Build scope",
        hotline: "Focus on the capability itself (core input → output logic); protocol provides surrounding infrastructure.",
        other: "Full product stack: accounts, subscriptions, billing, support, dashboard.",
        why: "Different supplier scales call for different build scopes and resource investment.",
      },
      {
        label: "Acquisition channel",
        hotline: "Marketplace public discovery + agent auto-matching.",
        other: "Self-built acquisition: SEO, paid ads, content, sales team.",
        why: "Individual capability suppliers need low-cost distribution channels.",
      },
      {
        label: "Customer management",
        hotline: "Caller Console provides a unified client-side control plane.",
        other: "Self-built customer portal with full customization.",
        why: "Different scenarios call for different levels of console customization.",
      },
      {
        label: "Pricing unit",
        hotline: "Per-call protocol settlement; platform handles invoicing.",
        other: "Plan, seat, or subscription based; full financial control.",
        why: "Individual suppliers need low-barrier financial infrastructure.",
      },
      {
        label: "Best-fit scenarios",
        hotline: "Input → output capabilities that agents can call directly.",
        other: "Complex B2B workflows, multi-tenant isolation, rich UI experiences.",
        why: "They target different product shapes — not a replacement relationship.",
      },
    ],
    whenHotline:
      "When your capability is input → output and can be called directly by agents; when you are an individual or small team looking to provide capabilities with minimal engineering overhead.",
    whenOther:
      "When your product is a complex UI workflow (design tools, document collaboration, whiteboards), needs multi-tenant isolation, or requires a full operations stack — SaaS is more mature for these scenarios.",
    faqs: [
      {
        question: "Can a SaaS company also use Hotline?",
        answer:
          "Absolutely. Expose input → output capabilities from your SaaS as Hotlines on the Marketplace — an additional agent channel without affecting the main product.",
      },
      {
        question: "Does Hotline mean there is no UI at all?",
        answer:
          "No. Consoles still matter for discovery, approvals, operations, and operator workflows. The difference is that the callable capability is the primary product unit.",
      },
      {
        question: "Why compare Hotline to SaaS?",
        answer:
          "Because many people try to map the concept to familiar software categories. The comparison clarifies that they serve different supplier populations and product shapes, and can coexist.",
      },
    ],
    nextLinks: NEXT_LINKS_DEFAULT,
  },
];

export function getEnglishComparePageBySlug(slug: EnglishCompareSlug) {
  return EN_COMPARE_PAGES.find((page) => page.slug === slug);
}

export function getEnglishComparePageByPath(path: string) {
  return EN_COMPARE_PAGES.find((page) => page.path === path);
}
